import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  clamp,
  distance,
  fmtMeters,
  gridStep,
  normalizeRect,
  randomPointInCircle,
  rectContains,
  screenToWorld,
  worldToScreen,
  type Vec2,
  type Viewport,
} from '../lib/geometry';
import { itemChanceAt, worldPoints, type Building, type Item, type LootData, type LootModel } from '../lib/loot';
import { gridRef, visibleTiles, type TileSource } from '../lib/maps';
import { dayzColorToCss, type Feature } from '../lib/model';
import { useDispatch, useFeatures, useStore } from '../state/store';

export type View = { cx: number; cz: number; scale: number };

type Props = {
  view: View;
  onViewChange: (view: View) => void;
  /** Background stretched over the whole terrain: the bundled official map or your own image. */
  mapImage: HTMLImageElement | null;
  /** Optional online tiles drawn over the background once loaded. */
  tileSource?: TileSource | null;
  /** Transparent tier-zone overlay, stretched over the terrain. */
  tierImage?: HTMLImageElement | null;
  loot?: LootData | null;
  lootModel?: LootModel | null;
  showBuildings?: boolean;
  selectedBuilding?: number | null;
  onSelectBuilding?: (index: number | null) => void;
  /** Item whose spawn chance is painted onto every building. */
  highlightItem?: Item | null;
};

/** Loot buildings appear once the map is zoomed in this far (pixels per metre). */
const BUILDINGS_MIN_SCALE = 0.06;
/** Individual loot points appear from this zoom. */
const POINTS_MIN_SCALE = 1.6;

export const TIER_COLORS = ['#46a758', '#f5c524', '#ff8028', '#e5484d', '#a457f5'];

/** Colour of a building's highest tier. */
export function tierColor(valueMask: number): string {
  for (let bit = 4; bit >= 0; bit -= 1) if (valueMask & (1 << bit)) return TIER_COLORS[bit];
  return '#8b98a5';
}

export function containerColor(name: string): string {
  const n = name.toLowerCase();
  if (n.includes('weapon')) return '#ff5c5c';
  if (n.includes('shel')) return '#4cb4ff';
  if (n.includes('floor') || n.includes('ground')) return '#7ee07e';
  return '#e0b0ff';
}

type Drag =
  | { kind: 'none' }
  /** `tap` is set for a one-finger touch drag: if the finger barely moves it counts as a tap. */
  | { kind: 'pan'; startScreen: Vec2Screen; startView: View; tap?: { world: Vec2; additive: boolean } }
  | { kind: 'pinch'; startDist: number; startMid: Vec2Screen; startView: View }
  | { kind: 'marquee'; start: Vec2; current: Vec2; additive: boolean }
  | { kind: 'move'; start: Vec2; current: Vec2 }
  | { kind: 'zone'; centre: Vec2; current: Vec2 }
  | { kind: 'radius'; featureId: string; centre: Vec2; current: Vec2 }
  | { kind: 'measure'; start: Vec2; current: Vec2 }
  | { kind: 'paint'; last: Vec2 };

type Vec2Screen = { sx: number; sy: number };

const HIT_PX = 8;
/** Fingers are less precise than a mouse. */
const TOUCH_HIT_PX = 18;
/** A touch that moves less than this (px) is a tap, not a pan. */
const TAP_SLOP_PX = 8;

export function MapCanvas({
  view,
  onViewChange,
  mapImage,
  tileSource = null,
  tierImage = null,
  loot = null,
  lootModel = null,
  showBuildings = false,
  selectedBuilding = null,
  onSelectBuilding,
  highlightItem = null,
}: Props) {
  const state = useStore();
  const dispatch = useDispatch();
  const { features, groups, byId } = useFeatures();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 800, height: 600 });
  const [drag, setDrag] = useState<Drag>({ kind: 'none' });
  const [cursor, setCursor] = useState<Vec2 | null>(null);
  const spaceHeld = useRef(false);
  const hitPx = useRef(HIT_PX);
  /** Active touch points (canvas coordinates), for pinch-zoom. */
  const touches = useRef(new Map<number, Vec2Screen>());
  const [touchInput, setTouchInput] = useState(() => window.matchMedia?.('(pointer: coarse)').matches ?? false);
  const tileCache = useRef(new Map<string, HTMLImageElement | 'error'>());
  const [tilesLoaded, setTilesLoaded] = useState(0);

  /** Spawn chance of the highlighted item in every building (0 where it cannot spawn). */
  const highlight = useMemo(() => {
    if (!loot || !lootModel || !highlightItem) return null;
    const chances = new Float32Array(loot.buildings.length);
    let max = 0;
    for (const b of loot.buildings) {
      chances[b.index] = itemChanceAt(lootModel, b, highlightItem);
      max = Math.max(max, chances[b.index]);
    }
    return { chances, max };
  }, [highlightItem, loot, lootModel]);

  const docById = useMemo(() => new Map(state.docs.map((d) => [d.id, d])), [state.docs]);
  /** `<territory color="...">` overrides the layer colour when present. */
  const groupColor = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of groups) if (group.color) map.set(group.id, group.color);
    return map;
  }, [groups]);

  const viewport: Viewport = useMemo(
    () => ({ cx: view.cx, cz: view.cz, scale: view.scale, width: size.width, height: size.height }),
    [view, size],
  );

  const visibleFeatures = useMemo(
    () => features.filter((f) => docById.get(f.docId)?.visible !== false),
    [features, docById],
  );

  /* ------------------------- sizing ------------------------- */
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width: Math.max(1, Math.round(width)), height: Math.max(1, Math.round(height)) });
    });
    observer.observe(wrap);
    return () => observer.disconnect();
  }, []);

  /* ------------------------- drawing ------------------------- */
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Phones report 3x; 2x is visually identical for a map and far cheaper to redraw.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (canvas.width !== size.width * dpr || canvas.height !== size.height * dpr) {
      canvas.width = size.width * dpr;
      canvas.height = size.height * dpr;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.width, size.height);

    // Terrain background.
    const topLeft = worldToScreen(viewport, 0, state.mapSize);
    const px = state.mapSize * viewport.scale;
    ctx.fillStyle = '#0d1117';
    ctx.fillRect(0, 0, size.width, size.height);
    ctx.fillStyle = '#16212e';
    ctx.fillRect(topLeft.sx, topLeft.sy, px, px);
    if (mapImage) {
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(mapImage, topLeft.sx, topLeft.sy, px, px);
    }
    if (tileSource) drawTiles(ctx, viewport, tileSource, tileCache.current, () => setTilesLoaded((n) => n + 1));
    if (tierImage) {
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(tierImage, topLeft.sx, topLeft.sy, px, px);
      ctx.imageSmoothingEnabled = true;
    }
    if (loot && (showBuildings || highlight)) {
      drawBuildings(ctx, viewport, loot, highlight, selectedBuilding);
    }

    if (state.settings.showGrid) drawGrid(ctx, viewport, state.mapSize);

    // Map border.
    ctx.strokeStyle = '#3a4a5c';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(topLeft.sx, topLeft.sy, px, px);

    const selected = new Set(state.selection);
    const dragOffset =
      drag.kind === 'move' ? { x: drag.current.x - drag.start.x, z: drag.current.z - drag.start.z } : null;

    // Zones first (circles), then points, so points stay clickable on top.
    for (const pass of ['zone', 'point'] as const) {
      for (const feature of visibleFeatures) {
        const isZone = feature.r !== undefined && feature.r > 0;
        if ((pass === 'zone') !== isZone) continue;
        const doc = docById.get(feature.docId);
        if (!doc) continue;
        const isSelected = selected.has(feature.id);
        const offset = isSelected && dragOffset ? dragOffset : { x: 0, z: 0 };
        const color = dayzColorToCss(groupColor.get(feature.groupId), doc.color);
        drawFeature(ctx, viewport, feature, offset, color, isSelected, state.settings.showLabels);
      }
    }

    // Live previews.
    if (drag.kind === 'zone' || drag.kind === 'radius') {
      const centre = drag.centre;
      const radius = distance(centre.x, centre.z, drag.current.x, drag.current.z);
      const s = worldToScreen(viewport, centre.x, centre.z);
      ctx.strokeStyle = '#ffd166';
      ctx.setLineDash([6, 4]);
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(s.sx, s.sy, radius * viewport.scale, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      label(ctx, s.sx, s.sy - 10, `r = ${fmtMeters(radius)} m`);
    }

    if (drag.kind === 'marquee') {
      const a = worldToScreen(viewport, drag.start.x, drag.start.z);
      const b = worldToScreen(viewport, drag.current.x, drag.current.z);
      ctx.fillStyle = 'rgba(59, 158, 255, 0.15)';
      ctx.strokeStyle = '#3b9eff';
      ctx.lineWidth = 1;
      ctx.fillRect(a.sx, a.sy, b.sx - a.sx, b.sy - a.sy);
      ctx.strokeRect(a.sx, a.sy, b.sx - a.sx, b.sy - a.sy);
    }

    if (drag.kind === 'measure') {
      const a = worldToScreen(viewport, drag.start.x, drag.start.z);
      const b = worldToScreen(viewport, drag.current.x, drag.current.z);
      ctx.strokeStyle = '#ffd166';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(a.sx, a.sy);
      ctx.lineTo(b.sx, b.sy);
      ctx.stroke();
      const d = distance(drag.start.x, drag.start.z, drag.current.x, drag.current.z);
      label(ctx, (a.sx + b.sx) / 2, (a.sy + b.sy) / 2 - 8, `${fmtMeters(d)} m`);
    }

    // Brush cursor.
    if ((state.tool === 'scatter' || state.tool === 'erase') && cursor) {
      const s = worldToScreen(viewport, cursor.x, cursor.z);
      ctx.strokeStyle = state.tool === 'erase' ? '#e5484d' : '#46a758';
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.arc(s.sx, s.sy, state.settings.brushRadius * viewport.scale, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Radius handle for a single selected zone.
    const single = state.selection.length === 1 ? byId.get(state.selection[0]) : undefined;
    if (single && single.r !== undefined && drag.kind === 'none') {
      const s = worldToScreen(viewport, single.x + single.r, single.z);
      ctx.fillStyle = '#ffd166';
      ctx.strokeStyle = '#1b1b1b';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(s.sx, s.sy, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
    // The selected building stays visible (with its loot points) at any zoom.
    if (loot && selectedBuilding !== null) drawSelectedBuilding(ctx, viewport, loot, loot.buildings[selectedBuilding]);
    // tilesLoaded is only a redraw trigger for tiles that finished downloading.
    void tilesLoaded;
  }, [byId, cursor, docById, drag, groupColor, highlight, loot, mapImage, selectedBuilding, showBuildings, size, state.mapSize, state.selection, state.settings, state.tool, tierImage, tileSource, tilesLoaded, viewport, visibleFeatures]);

  useEffect(() => {
    const frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [draw]);

  /* ------------------------- helpers ------------------------- */
  const toWorld = useCallback(
    (event: { clientX: number; clientY: number }): Vec2 => {
      const rect = canvasRef.current?.getBoundingClientRect();
      const sx = event.clientX - (rect?.left ?? 0);
      const sy = event.clientY - (rect?.top ?? 0);
      const world = screenToWorld(viewport, sx, sy);
      const snap = state.settings.snap;
      if (snap > 0) {
        return { x: Math.round(world.x / snap) * snap, z: Math.round(world.z / snap) * snap };
      }
      return world;
    },
    [viewport, state.settings.snap],
  );

  const hitTest = useCallback(
    (world: Vec2): Feature | null => {
      const tolerance = hitPx.current / viewport.scale;
      let best: Feature | null = null;
      let bestScore = Infinity;
      for (const feature of visibleFeatures) {
        const doc = docById.get(feature.docId);
        if (!doc || doc.locked) continue;
        const d = distance(feature.x, feature.z, world.x, world.z);
        if (d <= tolerance) {
          if (d < bestScore) {
            best = feature;
            bestScore = d;
          }
        } else if (feature.r !== undefined && d <= feature.r) {
          // Clicking inside a territory selects it, but a point on top wins.
          const score = tolerance + d;
          if (score < bestScore) {
            best = feature;
            bestScore = score;
          }
        }
      }
      return best;
    },
    [docById, visibleFeatures, viewport.scale],
  );

  const hitBuilding = useCallback(
    (world: Vec2): number | null => {
      if (!loot || !(showBuildings || highlight) || (!highlight && viewport.scale < BUILDINGS_MIN_SCALE)) return null;
      const tolerance = hitPx.current / viewport.scale;
      let best: number | null = null;
      let bestScore = Infinity;
      for (const b of loot.buildings) {
        if (highlight && !showBuildings && highlight.chances[b.index] === 0) continue;
        const d = distance(b.x, b.z, world.x, world.z);
        const reach = Math.max(loot.protos[b.proto].radius, tolerance);
        if (d <= reach && d < bestScore) {
          best = b.index;
          bestScore = d;
        }
      }
      return best;
    },
    [highlight, loot, showBuildings, viewport.scale],
  );

  const targetGroup = useCallback((): { docId: string; groupId: string } | null => {
    if (!state.activeDocId || !state.activeGroupId) return null;
    return { docId: state.activeDocId, groupId: state.activeGroupId };
  }, [state.activeDocId, state.activeGroupId]);

  const needTarget = useCallback(() => {
    dispatch({
      type: 'notice',
      kind: 'error',
      text: 'Pick a target layer and group in the Layers panel before drawing.',
    });
  }, [dispatch]);

  const eraseAt = useCallback(
    (world: Vec2) => {
      const radius = state.settings.brushRadius;
      const ids = visibleFeatures
        .filter((f) => {
          const doc = docById.get(f.docId);
          return doc && !doc.locked && distance(f.x, f.z, world.x, world.z) <= radius;
        })
        .map((f) => f.id);
      if (ids.length === 0) return;
      dispatch({ type: 'setSelection', ids });
      dispatch({ type: 'deleteSelection' });
    },
    [dispatch, docById, state.settings.brushRadius, visibleFeatures],
  );

  const scatterAt = useCallback(
    (world: Vec2) => {
      const target = targetGroup();
      if (!target) {
        needTarget();
        return;
      }
      const { brushRadius, brushSpacing } = state.settings;
      const existing = features.filter(
        (f) => distance(f.x, f.z, world.x, world.z) <= brushRadius + brushSpacing,
      );
      const created: { x: number; z: number }[] = [];
      for (let attempt = 0; attempt < 12 && created.length < 3; attempt += 1) {
        const candidate = randomPointInCircle(world.x, world.z, brushRadius);
        const clash =
          existing.some((f) => distance(f.x, f.z, candidate.x, candidate.z) < brushSpacing) ||
          created.some((p) => distance(p.x, p.z, candidate.x, candidate.z) < brushSpacing);
        if (!clash) created.push(candidate);
      }
      if (created.length > 0) {
        dispatch({ type: 'addPoints', docId: target.docId, groupId: target.groupId, points: created });
      }
    },
    [dispatch, features, needTarget, state.settings, targetGroup],
  );

  /** A click / tap on empty map: pick the loot building under it, else clear the selection. */
  const tapEmpty = (world: Vec2, additive: boolean): boolean => {
    if (!onSelectBuilding) return false;
    const building = hitBuilding(world);
    onSelectBuilding(building);
    if (building !== null && !additive) dispatch({ type: 'setSelection', ids: [] });
    return building !== null;
  };

  const canvasPoint = (event: { clientX: number; clientY: number }): Vec2Screen => {
    const rect = canvasRef.current?.getBoundingClientRect();
    return { sx: event.clientX - (rect?.left ?? 0), sy: event.clientY - (rect?.top ?? 0) };
  };

  const pinchState = () => {
    const [a, b] = [...touches.current.values()];
    return {
      dist: Math.max(Math.hypot(a.sx - b.sx, a.sy - b.sy), 1),
      mid: { sx: (a.sx + b.sx) / 2, sy: (a.sy + b.sy) / 2 },
    };
  };

  /* ------------------------- pointer ------------------------- */
  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    (event.target as Element).setPointerCapture(event.pointerId);
    const isTouch = event.pointerType === 'touch';
    if (isTouch !== touchInput) setTouchInput(isTouch);
    hitPx.current = isTouch ? TOUCH_HIT_PX : HIT_PX;
    const world = toWorld(event);

    if (isTouch) {
      touches.current.set(event.pointerId, canvasPoint(event));
      if (touches.current.size >= 2) {
        // A second finger always means pinch / two-finger pan; drop whatever the first one started.
        const { dist, mid } = pinchState();
        setDrag({ kind: 'pinch', startDist: dist, startMid: mid, startView: view });
        return;
      }
    }

    const panRequested = event.button === 1 || event.button === 2 || spaceHeld.current;

    if (panRequested) {
      setDrag({
        kind: 'pan',
        startScreen: { sx: event.clientX, sy: event.clientY },
        startView: view,
      });
      return;
    }
    if (event.button !== 0) return;

    switch (state.tool) {
      case 'select': {
        const single = state.selection.length === 1 ? byId.get(state.selection[0]) : undefined;
        if (single && single.r !== undefined) {
          const handle = worldToScreen(viewport, single.x + single.r, single.z);
          const rect = canvasRef.current?.getBoundingClientRect();
          const sx = event.clientX - (rect?.left ?? 0);
          const sy = event.clientY - (rect?.top ?? 0);
          if (Math.hypot(handle.sx - sx, handle.sy - sy) <= hitPx.current) {
            setDrag({ kind: 'radius', featureId: single.id, centre: { x: single.x, z: single.z }, current: world });
            return;
          }
        }
        const hit = hitTest(world);
        if (hit) {
          if (event.shiftKey) dispatch({ type: 'setSelection', ids: [hit.id], mode: 'toggle' });
          else if (!state.selection.includes(hit.id)) dispatch({ type: 'setSelection', ids: [hit.id] });
          setDrag({ kind: 'move', start: world, current: world });
        } else if (isTouch) {
          // One finger on empty map pans; a tap selects the building under it.
          setDrag({
            kind: 'pan',
            startScreen: { sx: event.clientX, sy: event.clientY },
            startView: view,
            tap: { world, additive: false },
          });
        } else {
          setDrag({ kind: 'marquee', start: world, current: world, additive: event.shiftKey });
        }
        return;
      }
      case 'point': {
        const target = targetGroup();
        if (!target) {
          needTarget();
          return;
        }
        dispatch({ type: 'addPoints', docId: target.docId, groupId: target.groupId, points: [world] });
        return;
      }
      case 'zone': {
        if (!targetGroup()) {
          needTarget();
          return;
        }
        setDrag({ kind: 'zone', centre: world, current: world });
        return;
      }
      case 'scatter':
        setDrag({ kind: 'paint', last: world });
        scatterAt(world);
        return;
      case 'erase':
        setDrag({ kind: 'paint', last: world });
        eraseAt(world);
        return;
      case 'measure':
        setDrag({ kind: 'measure', start: world, current: world });
        return;
    }
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const world = toWorld(event);
    setCursor(world);
    if (touches.current.has(event.pointerId)) touches.current.set(event.pointerId, canvasPoint(event));

    switch (drag.kind) {
      case 'pinch': {
        if (touches.current.size < 2) return;
        const { dist, mid } = pinchState();
        const scale = clamp((drag.startView.scale * dist) / drag.startDist, 0.004, 24);
        // Keep the world point that was under the fingers' midpoint under the new midpoint.
        const anchor = screenToWorld({ ...viewport, ...drag.startView }, drag.startMid.sx, drag.startMid.sy);
        onViewChange({
          scale,
          cx: anchor.x - (mid.sx - viewport.width / 2) / scale,
          cz: anchor.z - (viewport.height / 2 - mid.sy) / scale,
        });
        return;
      }
      case 'pan': {
        const dx = (event.clientX - drag.startScreen.sx) / view.scale;
        const dy = (event.clientY - drag.startScreen.sy) / view.scale;
        onViewChange({ ...drag.startView, cx: drag.startView.cx - dx, cz: drag.startView.cz + dy });
        return;
      }
      case 'marquee':
      case 'move':
      case 'measure':
        setDrag({ ...drag, current: world });
        return;
      case 'zone':
      case 'radius':
        setDrag({ ...drag, current: world });
        return;
      case 'paint': {
        const step = Math.max(state.settings.brushSpacing, 10);
        if (distance(drag.last.x, drag.last.z, world.x, world.z) < step) return;
        setDrag({ kind: 'paint', last: world });
        if (state.tool === 'erase') eraseAt(world);
        else scatterAt(world);
        return;
      }
      default:
        return;
    }
  };

  const onPointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    touches.current.delete(event.pointerId);
    if (drag.kind === 'pinch') {
      // Lifting either finger ends the pinch; the remaining one does nothing until lifted.
      setDrag({ kind: 'none' });
      return;
    }
    switch (drag.kind) {
      case 'pan': {
        const moved = Math.hypot(event.clientX - drag.startScreen.sx, event.clientY - drag.startScreen.sy);
        if (drag.tap && moved < TAP_SLOP_PX && !tapEmpty(drag.tap.world, drag.tap.additive)) {
          dispatch({ type: 'setSelection', ids: [] });
        }
        break;
      }
      case 'move': {
        const dx = drag.current.x - drag.start.x;
        const dz = drag.current.z - drag.start.z;
        if (Math.abs(dx) > 0.05 || Math.abs(dz) > 0.05) dispatch({ type: 'moveSelection', dx, dz });
        break;
      }
      case 'marquee': {
        const dragged = distance(drag.start.x, drag.start.z, drag.current.x, drag.current.z) * viewport.scale;
        // A plain click on empty map picks the loot building under the cursor, if any.
        if (dragged < 4 && tapEmpty(drag.current, drag.additive)) break;
        const rect = normalizeRect(drag.start, drag.current);
        const ids = visibleFeatures
          .filter((f) => {
            const doc = docById.get(f.docId);
            return doc && !doc.locked && rectContains(rect, f.x, f.z);
          })
          .map((f) => f.id);
        dispatch({ type: 'setSelection', ids, mode: drag.additive ? 'add' : 'replace' });
        break;
      }
      case 'zone': {
        const target = targetGroup();
        const radius = distance(drag.centre.x, drag.centre.z, drag.current.x, drag.current.z);
        if (target) {
          dispatch({
            type: 'addPoints',
            docId: target.docId,
            groupId: target.groupId,
            points: [{ ...drag.centre, r: Math.max(radius, 10) }],
          });
        }
        break;
      }
      case 'radius': {
        const radius = Math.max(distance(drag.centre.x, drag.centre.z, drag.current.x, drag.current.z), 1);
        dispatch({ type: 'setFeatureAttrs', ids: [drag.featureId], attrs: { r: radius.toFixed(1) } });
        break;
      }
      default:
        break;
    }
    setDrag({ kind: 'none' });
  };

  const onWheel = (event: React.WheelEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    const sx = event.clientX - (rect?.left ?? 0);
    const sy = event.clientY - (rect?.top ?? 0);
    const before = screenToWorld(viewport, sx, sy);
    const factor = Math.exp(-event.deltaY * 0.0015);
    const scale = clamp(view.scale * factor, 0.004, 24);
    const after = screenToWorld({ ...viewport, scale }, sx, sy);
    onViewChange({ scale, cx: view.cx + (before.x - after.x), cz: view.cz + (before.z - after.z) });
  };

  useEffect(() => {
    const down = (event: KeyboardEvent) => {
      if (event.code === 'Space') spaceHeld.current = true;
    };
    const up = (event: KeyboardEvent) => {
      if (event.code === 'Space') spaceHeld.current = false;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  const hover = useMemo(() => {
    if (!cursor || !loot || drag.kind !== 'none') return null;
    const index = hitBuilding(cursor);
    if (index === null) return null;
    const b = loot.buildings[index];
    const name = loot.protos[b.proto].name.replace(/^Land_/, '');
    if (highlight && highlightItem) return `${name} · ${highlightItem.name} ${(highlight.chances[index] * 100).toFixed(2)}%`;
    return name;
  }, [cursor, drag.kind, highlight, highlightItem, hitBuilding, loot]);

  const cursorStyle =
    drag.kind === 'pan' ? 'grabbing' : state.tool === 'select' ? 'default' : 'crosshair';

  return (
    <div className="map-wrap" ref={wrapRef}>
      <canvas
        ref={canvasRef}
        className="map-canvas"
        style={{ width: size.width, height: size.height, cursor: cursorStyle }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={(event) => {
          touches.current.delete(event.pointerId);
          setDrag({ kind: 'none' });
        }}
        onPointerLeave={() => setCursor(null)}
        onWheel={onWheel}
        onContextMenu={(event) => event.preventDefault()}
      />
      {tierImage && (
        <div className="tier-legend">
          {['Tier 1', 'Tier 2', 'Tier 3', 'Tier 4', 'Unique'].map((label, i) => (
            <span key={label}>
              <span className="dot" style={{ background: TIER_COLORS[i] }} />
              {label}
            </span>
          ))}
        </div>
      )}
      <div className="map-hud">
        <ScaleBar scale={viewport.scale} />
        {cursor ? (
          <>
            <span>
              x <b>{fmtMeters(cursor.x)}</b> &nbsp; z <b>{fmtMeters(cursor.z)}</b>
            </span>
            <span className="muted">grid {gridRef(cursor.x, cursor.z, state.mapSize)}</span>
            {hover && <span className="muted">{hover}</span>}
          </>
        ) : (
          <span className="muted">
            {touchInput ? 'drag to pan · pinch to zoom · tap a building for its loot' : 'move the pointer over the map'}
          </span>
        )}
      </div>
    </div>
  );
}

/** A metric scale bar so distances are readable without a map image. */
function ScaleBar({ scale }: { scale: number }) {
  const candidates = [1, 2, 5, 10, 20, 50, 100, 250, 500, 1000, 2500, 5000];
  const metres = candidates.find((value) => value * scale >= 60) ?? candidates[candidates.length - 1];
  return (
    <span className="scale-bar">
      <span className="scale-line" style={{ width: Math.round(metres * scale) }} />
      {metres >= 1000 ? `${metres / 1000} km` : `${metres} m`}
    </span>
  );
}

function drawFeature(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  feature: Feature,
  offset: { x: number; z: number },
  color: string,
  selected: boolean,
  showLabels: boolean,
) {
  const x = feature.x + offset.x;
  const z = feature.z + offset.z;
  const { sx, sy } = worldToScreen(viewport, x, z);
  if (sx < -200 || sy < -200 || sx > viewport.width + 200 || sy > viewport.height + 200) {
    if (feature.r === undefined || feature.r * viewport.scale < 50) return;
  }

  if (feature.r !== undefined && feature.r > 0) {
    const radius = feature.r * viewport.scale;
    ctx.beginPath();
    ctx.arc(sx, sy, radius, 0, Math.PI * 2);
    ctx.fillStyle = withAlpha(color, selected ? 0.28 : 0.16);
    ctx.fill();
    ctx.strokeStyle = selected ? '#ffd166' : color;
    ctx.lineWidth = selected ? 2.5 : 1.5;
    ctx.stroke();
  }

  ctx.beginPath();
  ctx.arc(sx, sy, selected ? 5 : 3.5, 0, Math.PI * 2);
  ctx.fillStyle = selected ? '#ffd166' : color;
  ctx.fill();
  ctx.strokeStyle = 'rgba(0,0,0,0.65)';
  ctx.lineWidth = 1;
  ctx.stroke();

  // Yaw indicator for event spawns.
  if (feature.a !== undefined && viewport.scale > 0.02) {
    const angle = (-feature.a * Math.PI) / 180;
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(sx + Math.sin(angle) * 12, sy - Math.cos(angle) * 12);
    ctx.strokeStyle = selected ? '#ffd166' : color;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  if (showLabels && feature.name && viewport.scale > 0.03) {
    ctx.font = '11px system-ui, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.fillText(feature.name, sx + 8, sy - 6);
  }
}

function drawGrid(ctx: CanvasRenderingContext2D, viewport: Viewport, mapSize: number) {
  const step = gridStep(viewport.scale);
  const topLeft = screenToWorld(viewport, 0, 0);
  const bottomRight = screenToWorld(viewport, viewport.width, viewport.height);
  const x0 = Math.max(0, Math.floor(topLeft.x / step) * step);
  const x1 = Math.min(mapSize, Math.ceil(bottomRight.x / step) * step);
  const z0 = Math.max(0, Math.floor(bottomRight.z / step) * step);
  const z1 = Math.min(mapSize, Math.ceil(topLeft.z / step) * step);

  ctx.lineWidth = 1;
  ctx.font = '10px system-ui, sans-serif';

  for (let x = x0; x <= x1; x += step) {
    const { sx } = worldToScreen(viewport, x, 0);
    ctx.strokeStyle = x % (step * 5) === 0 ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.06)';
    ctx.beginPath();
    ctx.moveTo(sx, 0);
    ctx.lineTo(sx, viewport.height);
    ctx.stroke();
    if (x % (step * 5) === 0) {
      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.fillText(String(x), sx + 3, 12);
    }
  }
  for (let z = z0; z <= z1; z += step) {
    const { sy } = worldToScreen(viewport, 0, z);
    ctx.strokeStyle = z % (step * 5) === 0 ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.06)';
    ctx.beginPath();
    ctx.moveTo(0, sy);
    ctx.lineTo(viewport.width, sy);
    ctx.stroke();
    if (z % (step * 5) === 0) {
      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.fillText(String(z), 3, sy - 3);
    }
  }
}

function label(ctx: CanvasRenderingContext2D, sx: number, sy: number, text: string) {
  ctx.font = '12px system-ui, sans-serif';
  const width = ctx.measureText(text).width + 10;
  ctx.fillStyle = 'rgba(12,16,22,0.85)';
  ctx.fillRect(sx - width / 2, sy - 16, width, 18);
  ctx.fillStyle = '#ffd166';
  ctx.textAlign = 'center';
  ctx.fillText(text, sx, sy - 3);
  ctx.textAlign = 'left';
}

function withAlpha(color: string, alpha: number): string {
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
    const value = Number.parseInt(full, 16);
    return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
  }
  if (color.startsWith('rgb(')) return color.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
  return color;
}

function drawTiles(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  source: TileSource,
  cache: Map<string, HTMLImageElement | 'error'>,
  onLoad: () => void,
) {
  const a = screenToWorld(viewport, 0, 0);
  const b = screenToWorld(viewport, viewport.width, viewport.height);
  const tiles = visibleTiles(source, viewport.scale, { x0: a.x, z0: b.z, x1: b.x, z1: a.z });
  for (const tile of tiles) {
    let image = cache.get(tile.url);
    if (!image) {
      // Keep memory bounded while panning around at high zoom (Map iterates oldest first).
      if (cache.size > 600) cache.delete(cache.keys().next().value as string);
      const img = new Image();
      img.decoding = 'async';
      img.referrerPolicy = 'no-referrer';
      img.onload = onLoad;
      img.onerror = () => cache.set(tile.url, 'error');
      img.src = tile.url;
      cache.set(tile.url, img);
      image = img;
    }
    if (image === 'error' || !image.complete || image.naturalWidth === 0) continue;
    const topLeft = worldToScreen(viewport, tile.x0, tile.z0 + tile.size);
    const px = tile.size * viewport.scale;
    // Half a pixel of overlap hides seams between neighbouring tiles.
    ctx.drawImage(image, topLeft.sx - 0.25, topLeft.sy - 0.25, px + 0.5, px + 0.5);
  }
}

function drawBuildings(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  loot: LootData,
  highlight: { chances: Float32Array; max: number } | null,
  selected: number | null,
) {
  if (!highlight && viewport.scale < BUILDINGS_MIN_SCALE) return;
  const margin = 40;
  const outline = viewport.scale >= 0.5;
  for (const b of loot.buildings) {
    if (b.index === selected) continue;
    const { sx, sy } = worldToScreen(viewport, b.x, b.z);
    if (sx < -margin || sy < -margin || sx > viewport.width + margin || sy > viewport.height + margin) continue;
    const proto = loot.protos[b.proto];

    if (highlight) {
      const chance = highlight.chances[b.index];
      if (chance <= 0) {
        if (viewport.scale >= BUILDINGS_MIN_SCALE) {
          ctx.fillStyle = 'rgba(139,152,165,0.35)';
          ctx.fillRect(sx - 1, sy - 1, 2, 2);
        }
        continue;
      }
      if (viewport.scale >= POINTS_MIN_SCALE) drawLootPoints(ctx, viewport, b, loot, 0.5);
      const strength = highlight.max > 0 ? Math.sqrt(chance / highlight.max) : 0;
      ctx.beginPath();
      ctx.arc(sx, sy, 2.5 + strength * 4.5, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, ${Math.round(220 - strength * 180)}, 60, ${0.55 + strength * 0.45})`;
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.6)';
      ctx.lineWidth = 1;
      ctx.stroke();
      continue;
    }

    const color = tierColor(b.valueMask);
    if (outline) {
      ctx.beginPath();
      ctx.arc(sx, sy, Math.max(proto.radius * viewport.scale, 4), 0, Math.PI * 2);
      ctx.strokeStyle = color;
      ctx.globalAlpha = 0.7;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    if (viewport.scale >= POINTS_MIN_SCALE) {
      drawLootPoints(ctx, viewport, b, loot, 0.85);
    } else {
      ctx.fillStyle = color;
      ctx.fillRect(sx - 1.5, sy - 1.5, 3, 3);
    }
  }
}

function drawLootPoints(ctx: CanvasRenderingContext2D, viewport: Viewport, b: Building, loot: LootData, alpha: number) {
  const proto = loot.protos[b.proto];
  const r = Math.min(Math.max(viewport.scale * 0.35, 1.5), 4);
  ctx.globalAlpha = alpha;
  for (const container of proto.containers) {
    ctx.fillStyle = containerColor(container.name);
    for (const p of worldPoints(b, container)) {
      const s = worldToScreen(viewport, p.x, p.z);
      ctx.beginPath();
      ctx.arc(s.sx, s.sy, r, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
}

function drawSelectedBuilding(ctx: CanvasRenderingContext2D, viewport: Viewport, loot: LootData, b: Building) {
  const proto = loot.protos[b.proto];
  const { sx, sy } = worldToScreen(viewport, b.x, b.z);
  ctx.beginPath();
  ctx.arc(sx, sy, Math.max(proto.radius * viewport.scale + 4, 9), 0, Math.PI * 2);
  ctx.strokeStyle = '#ffd166';
  ctx.lineWidth = 2.5;
  ctx.stroke();
  if (viewport.scale >= 0.5) drawLootPoints(ctx, viewport, b, loot, 1);
  // Facing: local +z of the building model.
  const t = (b.yaw * Math.PI) / 180;
  const len = Math.max(proto.radius * viewport.scale, 12);
  ctx.beginPath();
  ctx.moveTo(sx, sy);
  ctx.lineTo(sx - Math.sin(t) * len, sy - Math.cos(t) * len);
  ctx.strokeStyle = 'rgba(255,209,102,0.7)';
  ctx.lineWidth = 1.5;
  ctx.stroke();
}
